import { useCallback } from 'react';
import axios, { AxiosResponse } from 'axios';
import { useAuth } from '../contexts/AuthContext';

interface ApiResponse<T> {
  data: T;
  status: number;
  message?: string;
}

interface ApiMethods {
  get: <T>(url: string) => Promise<ApiResponse<T>>;
  post: <T>(url: string, data?: any) => Promise<ApiResponse<T>>;
  put: <T>(url: string, data?: any) => Promise<ApiResponse<T>>;
  delete: <T>(url: string) => Promise<ApiResponse<T>>;
  verifyToken: () => Promise<ApiResponse<{ valid: boolean }>>;
  refreshToken: () => Promise<ApiResponse<{ token: string }>>;
}

export const useApi = () => {
  const { isAuthenticated, refreshAuth } = useAuth();

  const callApi = useCallback(
    async <T>(
      apiCall: () => Promise<AxiosResponse<T>>,
      options: { requireAuth?: boolean; retryOnAuthError?: boolean } = {}
    ): Promise<ApiResponse<T>> => {
      const { requireAuth = true, retryOnAuthError = true } = options;

      if (requireAuth && !isAuthenticated) {
        throw new Error('Authentication required');
      }

      try {
        const response = await apiCall();
        return {
          data: response.data,
          status: response.status
        };
      } catch (error) {
        if (axios.isAxiosError(error)) {
          if (error.response?.status === 401 && retryOnAuthError) {
            try {
              await refreshAuth();
              const response = await apiCall();
              return {
                data: response.data,
                status: response.status
              };
            } catch (refreshError) {
              throw new Error('Authentication failed');
            }
          }
          throw new Error(error.response?.data?.message || 'API request failed');
        }
        throw error;
      }
    },
    [isAuthenticated, refreshAuth]
  );

  // Add authFetch for standard fetch API with authentication
  const authFetch = useCallback(
    async (url: string, options: RequestInit = {}): Promise<Response> => {
      if (!isAuthenticated) {
        throw new Error('Authentication required');
      }

      // Make the fetch request with authentication headers
      try {
        const response = await fetch(url, {
          ...options,
          headers: {
            ...options.headers,
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
        });

        // Handle 401 Unauthorized errors
        if (response.status === 401) {
          try {
            // Refresh the token
            await refreshAuth();
            
            // Retry the request with the new token
            return fetch(url, {
              ...options,
              headers: {
                ...options.headers,
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json',
              },
            });
          } catch (refreshError) {
            throw new Error('Authentication failed');
          }
        }

        return response;
      } catch (error) {
        console.error('Error in authFetch:', error);
        throw error;
      }
    },
    [isAuthenticated, refreshAuth]
  );

  const api: ApiMethods = {
    get: async <T>(url: string) => {
      return callApi<T>(() => axios.get(url));
    },
    post: async <T>(url: string, data?: any) => {
      return callApi<T>(() => axios.post(url, data));
    },
    put: async <T>(url: string, data?: any) => {
      return callApi<T>(() => axios.put(url, data));
    },
    delete: async <T>(url: string) => {
      return callApi<T>(() => axios.delete(url));
    },
    verifyToken: async () => {
      return callApi<{ valid: boolean }>(() => axios.get('/api/auth/verify'));
    },
    refreshToken: async () => {
      return callApi<{ token: string }>(() => axios.post('/api/auth/refresh'));
    }
  };

  return { callApi, isAuthenticated, api, authFetch };
}; 